import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { v4API } from '../../Api';
import { useV4 } from './V4Layout';
import { dateTime, errorMessage, StatusChip } from './common';

const sections = [
  ['orders', 'Ордера на ручной проверке'], ['outbox', 'Недоставленные колбеки мерчантам'],
  ['receipts', 'Проблемы доставки документов'], ['inbox', 'Входящие события на проверке'],
];

export default function Queues() {
  const { system } = useV4();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const load = useCallback(async () => {
    if (!system?.ready) return;
    const requestId = ++generation.current;
    setLoading(true); setError('');
    try { const response = await v4API.queues({ limit: 100 }); if (requestId === generation.current) setData(response); }
    catch (failure) { if (requestId === generation.current) setError(errorMessage(failure)); }
    finally { if (requestId === generation.current) setLoading(false); }
  }, [system?.ready]);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);
  async function retry() {
    if (!selected || busy) return;
    setBusy(true); setError(''); setMessage('');
    try {
      await v4API.retryCallback(selected.orderId, selected.eventId);
      setSelected(null); setMessage('Колбек поставлен в очередь повторной доставки. Начисление повторно не выполняется.');
      await load();
    } catch (failure) { setError(errorMessage(failure)); }
    finally { setBusy(false); }
  }
  if (!system?.ready) return <Alert severity="info">Очереди будут доступны после подготовки V4.</Alert>;
  return <Stack spacing={2}>
    <Stack direction="row" justifyContent="space-between"><Typography variant="h5" component="h2">Очереди и ручная проверка</Typography><Button onClick={load} disabled={loading || busy}>Обновить</Button></Stack>
    <Typography variant="body2" color="text.secondary">До 100 записей в каждом разделе. Неизвестный результат не равен отказу: сначала сверяйте платёж с провайдером.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {message && <Alert severity="success" onClose={() => setMessage('')}>{message}</Alert>}
    {loading && <CircularProgress size={24} aria-label="Загрузка очередей" />}
    {sections.map(([key, title]) => <Card key={key} variant="outlined"><CardContent>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <TableContainer><Table size="small" aria-label={title}><TableHead><TableRow><TableCell>Идентификатор</TableCell><TableCell>Состояние</TableCell><TableCell>Причина</TableCell><TableCell>Попытки</TableCell><TableCell>Обновлено</TableCell><TableCell>Действия</TableCell></TableRow></TableHead><TableBody>
        {(data?.[key] || []).map(row => {
          const orderId = key === 'orders' ? row._id : row.orderId;
          return <TableRow key={row._id}><TableCell sx={{ fontFamily: 'monospace', maxWidth: 250, overflowWrap: 'anywhere' }}>{row.eventId || row.receiptId || row._id}</TableCell><TableCell><StatusChip status={row.status} /></TableCell><TableCell sx={{ overflowWrap: 'anywhere' }}>{row.reviewReason || [row.lastErrorCode, row.lastErrorDetail?.httpStatus, row.lastErrorDetail?.reason, row.lastErrorDetail?.url].filter(value => value != null && value !== '').join(' · ') || '—'}</TableCell><TableCell>{row.attempts ?? '—'}</TableCell><TableCell>{dateTime(row.updatedAt)}</TableCell><TableCell><Stack spacing={1}>
            {orderId && <Button component={Link} to={`/v4/orders/${encodeURIComponent(orderId)}`} size="small">Ордер</Button>}
            {key === 'outbox' && orderId && row.eventId && <Button onClick={() => setSelected(row)} size="small" disabled={busy}>Повторить колбек</Button>}
          </Stack></TableCell></TableRow>;
        })}
        {data && !data[key]?.length && <TableRow><TableCell colSpan={6}>Проблемных записей нет.</TableCell></TableRow>}
      </TableBody></Table></TableContainer>
    </CardContent></Card>)}
    <Dialog open={!!selected} onClose={() => !busy && setSelected(null)} maxWidth="sm" fullWidth><DialogTitle>Повторить доставку колбека?</DialogTitle><DialogContent><Typography>Повторно отправится существующее событие, а не создастся новый платёж. Ответ мерчанта обрабатывается по сохранённой политике, включая разрешённый legacy-перевод покупки в депозит. Повторное зачисление защищено.</Typography></DialogContent><DialogActions><Button disabled={busy} onClick={() => setSelected(null)}>Отмена</Button><Button variant="contained" disabled={busy} onClick={retry}>{busy ? 'Постановка в очередь…' : 'Повторить доставку'}</Button></DialogActions></Dialog>
  </Stack>;
}
